import React from 'react';
import { Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { Standort } from 'apiClient/Api';
import { OfferPosition, OfferSummary } from './calculatorApi';
import { CalcImage, CalcImageData } from './CalcImage';

const money = (v: number) =>
    Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const positionImages = (position: OfferPosition): CalcImageData[] =>
    (position.images || []).map((img) => ({
        articleNumber: position.articleNumber,
        name: img.ref.split('/').pop() || img.ref,
        titel: img.titel,
        beschreibung: img.beschreibung,
        haupt: img.haupt,
        crop: img.crop,
    }));

interface Section {
    header: OfferPosition | null;
    items: OfferPosition[];
    summary: OfferPosition | null;
}

const buildSections = (positions: OfferPosition[]): Section[] => {
    const sections: Section[] = [];
    let current: Section | null = null;
    for (const p of positions) {
        if (p.kind === 'GroupHeader') {
            current = { header: p, items: [], summary: null };
            sections.push(current);
        } else if (p.kind === 'Summary') {
            if (current) current.summary = p;
            else sections.push({ header: null, items: [], summary: p });
        } else {
            if (!current) {
                current = { header: null, items: [], summary: null };
                sections.push(current);
            }
            current.items.push(p);
        }
    }
    return sections;
};

/** Eine Warenkorb-Zeile: Bild · Menge · Beschreibung/Artikel · Einzel-/Gesamtpreis. */
const CartLine: React.FC<{ position: OfferPosition; standortLabel?: string }> = ({ position, standortLabel }) => {
    const images = positionImages(position);
    const hidden = position.kind === 'Hidden';

    return (
        <Stack
            direction="row"
            spacing={2}
            sx={{
                alignItems: 'center',
                py: 1.25,
                px: 1.5,
                opacity: hidden ? 0.7 : 1,
            }}
        >
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    overflow: 'hidden',
                    color: 'text.disabled',
                }}
            >
                {images.length > 0 ? <CalcImage image={images[0]} height={48} /> : <FontAwesomeIcon icon={faBoxOpen} />}
            </Box>

            <Chip
                size="small"
                color={hidden ? 'default' : 'primary'}
                variant={hidden ? 'outlined' : 'filled'}
                label={`${position.quantity}×`}
                sx={{ flexShrink: 0, fontWeight: 600 }}
            />

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                    {position.description}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    {position.articleNumber && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {position.articleNumber}
                        </Typography>
                    )}
                    {standortLabel && (
                        <Chip size="small" variant="outlined" label={standortLabel} sx={{ height: 18 }} />
                    )}
                    {position.isOption && (
                        <Chip size="small" color="info" variant="outlined" label="optional" sx={{ height: 18 }} />
                    )}
                    {position.priceBreakdown.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            <FontAwesomeIcon icon={faCircleInfo} style={{ marginRight: 4 }} />
                            {position.priceBreakdown.map((b) => `${b.quantity}× ${money(b.unitPrice)}`).join(' + ')}
                        </Typography>
                    )}
                </Stack>
            </Box>

            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                {position.quantity > 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {money(position.unitPrice)} / Stk.
                    </Typography>
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {money(position.totalPrice)}
                </Typography>
            </Box>
        </Stack>
    );
};

/** Warenkorb-Darstellung des kalkulierten Angebots aus den Positionen. */
export const Warenkorb: React.FC<{ positions: OfferPosition[]; summary: OfferSummary; standorte: Standort[] }> = ({
    positions,
    summary,
    standorte,
}) => {
    const sections = buildSections(positions);

    // Standort-Id → Name; Chip nur, wenn es überhaupt mehrere Standorte gibt.
    const multiStandort = standorte.length > 1;
    const nameById = new Map(standorte.map((s) => [s.id, s.name || s.id]));
    const standortLabel = (area: string): string | undefined =>
        multiStandort && area ? nameById.get(area) ?? area : undefined;

    if (sections.length === 0) {
        return (
            <Typography color="text.secondary" sx={{ p: 2 }}>
                Keine Positionen.
            </Typography>
        );
    }

    return (
        <Stack spacing={2}>
            {sections.map((section, si) => (
                <Paper key={si} variant="outlined" sx={{ overflow: 'hidden' }}>
                    {section.header && (
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1.5,
                                py: 1,
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {section.header.description}
                            </Typography>
                            {section.header.totalPrice > 0 && (
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    {money(section.header.totalPrice)}
                                </Typography>
                            )}
                        </Stack>
                    )}
                    {section.items.map((item, ii) => (
                        <React.Fragment key={ii}>
                            {ii > 0 && <Divider />}
                            <CartLine position={item} standortLabel={standortLabel(item.area)} />
                        </React.Fragment>
                    ))}
                    {section.summary && (
                        <>
                            <Divider />
                            <Stack
                                direction="row"
                                sx={{ alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, bgcolor: 'action.hover' }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {section.summary.description}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {money(section.summary.totalPrice)}
                                </Typography>
                            </Stack>
                        </>
                    )}
                </Paper>
            ))}

            <Paper
                sx={{
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'success.main',
                    color: 'success.contrastText',
                }}
            >
                <Typography variant="h6">Gesamt</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {money(summary.gesamt)}
                </Typography>
            </Paper>
        </Stack>
    );
};
